import { MarkerType, type Edge, type Node } from "@xyflow/react";
import { transformWorkflow } from "./workflow-graph";
import { layoutGraph, type LayoutDirection } from "./workflow-layout";

export type CanvasDirection = LayoutDirection;

export interface CanvasItemPlacement {
  portalId: string;
  flowId: string;
  positionX: number;
  positionY: number;
  direction?: CanvasDirection;
}

export interface WorkflowDefinitionLike {
  name?: string;
  startActionId?: string;
  actions?: { actionId: string; [key: string]: unknown }[];
  enrollmentCriteria?: Record<string, unknown>;
  isEnabled?: boolean;
}

const GROUP_PADDING_X = 24;
const GROUP_PADDING_TOP = 56;
const GROUP_PADDING_BOTTOM = 24;
const NODE_WIDTH = 160;
const NODE_HEIGHT = 160;

export interface BuildResult {
  nodes: Node[];
  edges: Edge[];
}

export function buildCanvasGraph(
  items: CanvasItemPlacement[],
  definitions: Record<string, WorkflowDefinitionLike | undefined>,
): BuildResult {
  const nodes: Node[] = [];
  const edges: Edge[] = [];

  for (const item of items) {
    const def = definitions[item.flowId];
    if (!def) continue;

    const direction: CanvasDirection = item.direction ?? "TB";
    const graph = transformWorkflow(def as never);
    const laid = layoutGraph(graph.nodes, graph.edges, direction);

    let minX = Infinity;
    let minY = Infinity;
    let maxX = -Infinity;
    let maxY = -Infinity;
    for (const n of laid) {
      minX = Math.min(minX, n.position.x);
      minY = Math.min(minY, n.position.y);
      maxX = Math.max(maxX, n.position.x + NODE_WIDTH);
      maxY = Math.max(maxY, n.position.y + NODE_HEIGHT);
    }
    if (!Number.isFinite(minX)) {
      minX = 0;
      minY = 0;
      maxX = NODE_WIDTH;
      maxY = NODE_HEIGHT;
    }

    const innerWidth = maxX - minX;
    const innerHeight = maxY - minY;
    const groupId = `group:${item.flowId}`;

    nodes.push({
      id: groupId,
      type: "workflowGroup",
      position: { x: item.positionX, y: item.positionY },
      data: {
        flowId: item.flowId,
        name: def.name ?? item.flowId,
        isEnabled: def.isEnabled,
        direction,
      },
      style: {
        width: innerWidth + GROUP_PADDING_X * 2,
        height: innerHeight + GROUP_PADDING_TOP + GROUP_PADDING_BOTTOM,
      },
      draggable: true,
      selectable: true,
    });

    for (const n of laid) {
      nodes.push({
        id: `${item.flowId}::${n.id}`,
        type: n.type,
        parentId: groupId,
        extent: "parent",
        position: {
          x: n.position.x - minX + GROUP_PADDING_X,
          y: n.position.y - minY + GROUP_PADDING_TOP,
        },
        data: {
          ...(n.data as unknown as Record<string, unknown>),
          direction,
        },
        draggable: false,
        selectable: false,
      });
    }

    for (const e of graph.edges) {
      edges.push({
        id: `${item.flowId}::${e.id}`,
        source: `${item.flowId}::${e.source}`,
        target: `${item.flowId}::${e.target}`,
        label: e.label,
        markerEnd: {
          type: MarkerType.ArrowClosed,
          width: 18,
          height: 18,
          color: "#6b6f80",
        },
        style: { stroke: "#6b6f80", strokeWidth: 1.5 },
      });
    }
  }

  return { nodes, edges };
}
